"use client";

import { Fragment, useRef } from "react";
import MessageBubble, { type ChatMessage } from "./MessageBubble";
import Composer from "./chatThread/Composer";
import { seenKeyFor } from "./chatThread/seenMessages";
import UnreadDivider from "./chatThread/UnreadDivider";
import { useMessagePoll } from "./chatThread/useMessagePoll";
import { useMessageThread } from "./chatThread/useMessageThread";
import { useStickyScroll } from "./chatThread/useStickyScroll";
import { useStreamedReply } from "./chatThread/useStreamedReply";
import WorkingIndicator from "./chatThread/WorkingIndicator";
import WorkingSteps from "./chatThread/WorkingSteps";
import { workingSteps } from "@/lib/chat/workingSteps";

/**
 * One conversation with a worker, or with Smithers across all of them. The
 * behaviour lives in the chatThread hooks — the message list and its poll,
 * the streamed reply, following the bottom — and this component wires them
 * together and lays out the thread, the "New messages" pill and the composer.
 */
export default function ChatThread({
  workerId,
  businessId,
  visible = true,
  workerName,
  initialMessages,
  emptyHint,
  inputId,
  setupWarning,
}: {
  /** null = Smithers' global cross-worker thread. */
  workerId: string | null;
  businessId: string;
  visible?: boolean;
  workerName: string;
  initialMessages: ChatMessage[];
  emptyHint?: string;
  /** Optional id for focusing the composer from elsewhere. */
  inputId?: string;
  /** Blocking setup problem shown before the user types (e.g. no API key). */
  setupWarning?: string | null;
}) {
  const seenKey = seenKeyFor(workerId, businessId);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const thread = useMessageThread({ initialMessages, seenKey, visible });
  const history = useMessagePoll({ workerId, businessId, visible, scrollerRef, thread });
  const reply = useStreamedReply({ workerId, businessId, thread, history });
  // Smithers' thread draws his work as steps; a worker's thread keeps the working line.
  const { working, processes, turnStartedAt } = history;
  const turn = workerId ? "idle" : reply.sending && reply.streamingText ? "replying" : (reply.sending && !reply.streamingText) || (working && !reply.sending) ? "thinking" : "idle";
  const steps = workerId ? [] : workingSteps({ processes, turn, turnStartedAt });
  const scroll = useStickyScroll({
    scrollerRef,
    scrollRestoreRef: history.scrollRestoreRef,
    thread,
    streamingText: reply.streamingText,
    sending: reply.sending,
    followKey: steps.map((step) => `${step.key}:${step.state}`).join(","),
  });
  const { msgs, hasUnread, unreadFromId } = thread;
  const { hasOlder, loadingOlder, historyError, loadOlder, clock } = history;
  const { input, setInput, sending, sendError, setSendError, streamingText, streamStartedAt, send, stop } = reply;
  const { bottomRef, handleScroll, jumpToLatest } = scroll;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={scrollerRef}
          onScroll={handleScroll}
          className="flex-1 space-y-3.5 sm:space-y-4 overflow-y-auto px-3 sm:px-4 py-3 sm:py-4"
        >
          {hasOlder && (
            <div className="text-center">
              <button type="button" disabled={loadingOlder || sending} onClick={() => void loadOlder()} className="rounded-lg border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800 disabled:opacity-50">
                {loadingOlder ? "Loading…" : "Load older messages"}
              </button>
            </div>
          )}
          {historyError && <p role="alert" className="text-sm text-red-300">{historyError}</p>}
          {msgs.length === 0 && streamingText === null && !sending && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <p className="max-w-sm text-sm text-zinc-400">
                {emptyHint ??
                  "Ask a question, request config changes, or brainstorm new automations."}
              </p>
            </div>
          )}
          {msgs.map((m) => (
            <Fragment key={m.id}>
              {m.id === unreadFromId && <UnreadDivider />}
              <MessageBubble message={m} />
            </Fragment>
          ))}
          {!workerId && <WorkingSteps steps={steps} processes={processes} clock={clock} />}
          {workerId && ((sending && !streamingText) || (working && !sending)) && <WorkingIndicator name={workerName} />}
          {streamingText ? (
            <MessageBubble
              message={{
                id: "streaming",
                role: "assistant",
                content: streamingText,
                source: "chat",
                createdAt: streamStartedAt,
                streaming: true,
              }}
            />
          ) : null}
          <div ref={bottomRef} />
        </div>

        {hasUnread && (
          <button
            type="button"
            onClick={jumpToLatest}
            aria-label="Jump to the latest message"
            className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-800/95 px-3 py-1.5 text-xs font-medium text-zinc-100 shadow-lg backdrop-blur transition hover:border-zinc-600 hover:bg-zinc-700"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden
              className="size-3.5"
            >
              <path
                d="M8 3v10m0 0 4-4m-4 4-4-4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            New messages
          </button>
        )}
      </div>

      <Composer
        inputId={inputId}
        workerName={workerName}
        input={input}
        onInputChange={setInput}
        onSend={send}
        onStop={stop}
        sending={sending}
        sendError={sendError}
        onDismissError={() => setSendError(null)}
        setupWarning={setupWarning}
      />
    </div>
  );
}
